import type { ProductFeatureGroup } from "@itaas/pep-contracts";
import type {
  ProductAudienceItem,
  ProductDetailHdConfig,
  ProductOutcomeItem,
  ProductTrustChip,
} from "./product-detail-types";

export const CRM_HERO_PRODUCT_ASSET =
  "/assets/products/crm/hero/itaas-crm-product-dashboard.png";
export const CRM_HERO_HUMAN_ASSET =
  "/assets/products/crm/hero/itaas-crm-sales-professional.png?v=1";

/** Actors grounded in CRM SoT + product UI (commercial ops before Invoice). */
export const CRM_AUDIENCES: ProductAudienceItem[] = [
  {
    id: "direction",
    icon: "🏛️",
    titleFr: "Direction",
    titleEn: "Leadership",
    bodyFr: "Visualisez pipeline, devis et performance commerciale depuis un tableau de bord unique.",
    bodyEn: "See pipeline, quotes and sales performance from one dashboard.",
  },
  {
    id: "sales-lead",
    icon: "📈",
    titleFr: "Direction commerciale",
    titleEn: "Sales leadership",
    bodyFr: "Pilotez opportunités par étape, montants estimés et priorités d’équipe.",
    bodyEn: "Steer opportunities by stage, estimated amounts and team priorities.",
  },
  {
    id: "reps",
    icon: "🤝",
    titleFr: "Commerciaux",
    titleEn: "Sales reps",
    bodyFr: "Suivez contacts, activités et devis sans perdre le fil entre prospect et closing.",
    bodyEn: "Track contacts, activities and quotes without losing the thread from lead to close.",
  },
  {
    id: "sales-admin",
    icon: "📦",
    titleFr: "Administration des ventes",
    titleEn: "Sales administration",
    bodyFr: "Structurez devis, bons de livraison et expéditions dans le parcours commercial.",
    bodyEn: "Structure quotes, delivery notes and shipments in the sales journey.",
  },
  {
    id: "cx",
    icon: "💬",
    titleFr: "Relation client",
    titleEn: "Customer success",
    bodyFr: "Conservez l’historique contacts / organisations et un accès portail devis côté client.",
    bodyEn: "Keep contact / organization history and a client-side quote portal.",
  },
];

export const CRM_OUTCOMES: ProductOutcomeItem[] = [
  {
    id: "pipeline",
    icon: "🎯",
    titleFr: "Pipeline visible",
    titleEn: "Visible pipeline",
    bodyFr: "Toute l’équipe commerciale voit où en est chaque opportunité — étapes, montant, probabilité.",
    bodyEn: "The whole sales team sees where each opportunity stands — stage, amount, probability.",
  },
  {
    id: "quote",
    icon: "📄",
    titleFr: "Devis → facture",
    titleEn: "Quote → invoice",
    bodyFr: "Convertissez un devis commercial en facture ITaaS Invoice sans ressaisie.",
    bodyEn: "Convert a sales quote to an ITaaS Invoice without re-entry.",
  },
  {
    id: "leads",
    icon: "📣",
    titleFr: "Leads mieux captés",
    titleEn: "Better lead capture",
    bodyFr: "Les demandes du site ITaaS (démo, contact) peuvent alimenter votre pipeline.",
    bodyEn: "ITaaS site requests (demo, contact) can feed your pipeline.",
  },
  {
    id: "suite",
    icon: "🧩",
    titleFr: "Avant la facturation",
    titleEn: "Before invoicing",
    bodyFr: "Complète ITaaS Invoice sans le remplacer — du premier contact au paiement.",
    bodyEn: "Complements ITaaS Invoice without replacing it — from first contact to payment.",
  },
];

/**
 * Capabilities: catalog SoT + commercial modules visible on the real CRM dashboard.
 * Do not claim trial (portfolio trialAllowed=false) or SaaS fluff.
 */
export const CRM_CAPABILITY_GROUPS: ProductFeatureGroup[] = [
  {
    titleFr: "Contacts & pipeline",
    titleEn: "Contacts & pipeline",
    features: [
      "Contacts et organisations",
      "Pipeline d’opportunités visuel",
      "Activités et suivi commercial",
      "Historique d’interactions",
    ],
  },
  {
    titleFr: "Devis & conversion",
    titleEn: "Quotes & conversion",
    features: [
      "Devis commerciaux",
      "Conversion vers ITaaS Invoice",
      "Montants et probabilités par étape",
    ],
  },
  {
    titleFr: "Opérations commerciales",
    titleEn: "Sales operations",
    features: [
      "Bons de livraison",
      "Expéditions",
      "Points fidélité",
      "Rapports devis et ventes",
    ],
  },
  {
    titleFr: "Suite & portail",
    titleEn: "Suite & portal",
    features: [
      "Portail client — devis",
      "Capture leads depuis le site PEP",
      "Notifications de relance",
      "Handoff Suite (Kanakku)",
    ],
  },
];

const CRM_TRUST_BASE: ProductTrustChip[] = [
  {
    id: "suite",
    labelFr: "Module commercial de la Suite ITaaS",
    labelEn: "ITaaS Suite commercial module",
  },
  {
    id: "invoice",
    labelFr: "Continuité devis → ITaaS Invoice",
    labelEn: "Quote → ITaaS Invoice continuity",
  },
  {
    id: "market",
    labelFr: "Pensé pour PME et équipes B2B",
    labelEn: "Built for SMEs and B2B teams",
  },
];

export const CRM_HD_CONFIG: ProductDetailHdConfig = {
  productImageSrc: CRM_HERO_PRODUCT_ASSET,
  productImageAlt: {
    fr: "Tableau de bord ITaaS CRM — pipeline, contacts, devis et activités",
    en: "ITaaS CRM dashboard — pipeline, contacts, quotes and activities",
  },
  humanImageSrc: CRM_HERO_HUMAN_ASSET,
  humanImageAlt: {
    fr: "Professionnel commercial utilisant ses outils numériques",
    en: "Sales professional using digital tools",
  },
  visualCaption: {
    fr: "Interface produit ITaaS CRM — tableau de bord commercial",
    en: "ITaaS CRM product interface — sales dashboard",
  },
  audiences: CRM_AUDIENCES,
  audienceTitle: {
    fr: "Un CRM pour chaque acteur de la chaîne commerciale",
    en: "A CRM for every stakeholder in the sales chain",
  },
  outcomes: CRM_OUTCOMES,
  outcomesLead: {
    fr: "Des bénéfices concrets pour piloter prospects, opportunités et devis.",
    en: "Practical benefits for running leads, opportunities and quotes.",
  },
  capabilityGroups: CRM_CAPABILITY_GROUPS,
  preferConfigCapabilities: true,
  contextTitle: {
    fr: "Relation client avant la facturation",
    en: "Customer relationship before invoicing",
  },
  contextLead: {
    fr: "Structuré pour les équipes commerciales en Afrique francophone et au-delà — sans affirmation commerciale non ouverte côté catalogue.",
    en: "Built for sales teams in Francophone Africa and beyond — without promoting catalog paths that are not open.",
  },
  contextFallbackPoints: [
    {
      fr: "Pipeline, contacts et devis dans un module dédié",
      en: "Pipeline, contacts and quotes in a dedicated module",
    },
    {
      fr: "Conversion devis vers ITaaS Invoice sans ressaisie",
      en: "Quote conversion to ITaaS Invoice without re-entry",
    },
    {
      fr: "Complète la facturation — ne la remplace pas",
      en: "Complements invoicing — does not replace it",
    },
  ],
  trustBaseChips: CRM_TRUST_BASE,
  showAmbassadorLink: false,
  showEditionShowcase: false,
  ctaPriority: "demo",
};
